import React, { useId } from 'react';
import { DOODLES, type DoodleName } from './doodleArt';
import './doodle.css';

/**
 * A drawing of one desk object.
 *
 * These are engravings: a contour at one confident weight, cross-hatching that
 * follows the form to describe the shadow, and one flat tint behind. Light comes
 * from the upper left in every one of them, and every object sits at a slight
 * angle, so they read as things lying on a desk rather than elevations of a shape.
 *
 * Nothing here is roughened. The previous set was primitive geometry — a tag was a
 * pentagon with a circle in it — pushed through a wobble to make it look drawn,
 * and a wobble on a perfect shape reads as a shaky perfect shape. The fix was not
 * a better wobble; it was to draw the object. So the art is hand-authored in
 * doodleArt.ts and rendered exactly as written, with no build step in between.
 *
 * The line takes `currentColor` (the desk's navy) and the tint takes the chapter
 * tone, so the same object prints blue on Scout, amber on Deals, pink on Sponsors.
 *
 * Where it goes: an empty desk (nothing filed yet), a dialog's head, an onboarding
 * step — at 40px and up, which is what the hatching needs. Below that, a folder tab
 * or a row, the mark is stamped instead: see NavMark. Never in a list or a grid.
 */
export default function Doodle({ name, size = 'md', className = '' }: {
  name: DoodleName;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const art = DOODLES[name];
  const id = useId().replace(/:/g, '');
  return (
    <svg className={`br-doodle br-doodle--${size} ${className}`} viewBox="0 0 64 64" aria-hidden="true" focusable="false">
      <defs>
        {/* the tint breaks up as it prints: fine noise punched through the mass */}
        <filter id={id} x="-30%" y="-30%" width="160%" height="160%">
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="11" result="speck" />
          <feColorMatrix in="speck" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  1 0 0 0 .15" result="grain" />
          <feComposite in="SourceGraphic" in2="grain" operator="in" />
        </filter>
      </defs>
      <path className="br-doodle__wash" d={art.wash} filter={`url(#${id})`} />
      <g className="br-doodle__hatch">
        {art.hatch.map((d, i) => <path key={i} d={d} />)}
      </g>
      <g className="br-doodle__ink">
        {art.ink.map((d, i) => <path key={i} d={d} />)}
      </g>
    </svg>
  );
}

export type { DoodleName };
